/**
 * Provisioning Orchestrator Schemas
 *
 * Types for the multi-step new-school onboarding state machine that
 * automates Apple/Google/EAS/OneSignal/Datadog API calls.
 *
 * Plan: docs/_archive/2026-10-17-plans-sweep/plans/2026-05-09-003-feat-mobile-provisioning-orchestrator-plan.md (U2)
 */

import { z } from 'zod'

// --- Enums ---

export const ProvisioningRunStatusSchema = z.enum([
  'PENDING',
  'IN_PROGRESS',
  'SUCCEEDED',
  'FAILED',
  'MANUAL_INTERVENTION_REQUIRED'
])
export type ProvisioningRunStatus = z.infer<typeof ProvisioningRunStatusSchema>

export const ProvisioningStepStatusSchema = z.enum([
  'PENDING',
  'IN_PROGRESS',
  'SUCCEEDED',
  'FAILED',
  'SKIPPED',
  'MANUAL_INTERVENTION_REQUIRED',
  'MANUALLY_COMPLETED'
])
export type ProvisioningStepStatus = z.infer<typeof ProvisioningStepStatusSchema>

// --- Run inputs ---
//
// Shape collected by the admin wizard. Kept loose enough at this layer to
// accept partial/forward-compatible inputs; per-step validation lives in U3.

/**
 * App Store Connect API key uploaded by the operator. Vaulted on first
 * step run, then cleared from the run inputs.
 */
export const AscApiKeyInputSchema = z.object({
  issuerId: z.string().min(1),
  keyId: z.string().min(1),
  /** PEM-encoded P-256 EC private key. */
  privateKey: z.string().min(1)
})
export type AscApiKeyInput = z.infer<typeof AscApiKeyInputSchema>

/**
 * Apple Push Notification Service auth key (.p8) bundle. Used to
 * configure OneSignal's iOS channel.
 */
export const ApnsAuthKeyInputSchema = z.object({
  /** PEM-encoded .p8 contents. */
  p8: z.string().min(1),
  keyId: z.string().min(1),
  teamId: z.string().min(1),
  /**
   * The push topic this key is scoped to — the app's bundle id.
   *
   * An APNs key is registered topic-scoped, so one Apple team holds one key PER
   * APP rather than one key overall. Without the topic on the row, an
   * account-keyed read can only answer "does this team have a key", never
   * "which of this team's keys is this school's" — see
   * `getApnsAccountCredential` in the backend.
   *
   * Optional so rows written before the field existed stay valid; the resolver
   * accepts a lone untagged row as the team's key.
   */
  bundleId: z.string().min(1).optional()
})
export type ApnsAuthKeyInput = z.infer<typeof ApnsAuthKeyInputSchema>

export const ProvisioningRunInputsSchema = z.object({
  // --- Identity ---
  schoolId: z.string().min(1),
  brandName: z.string().min(1),
  // The App Store / Google Play listing title (`MobileApp.displayName`), not the on-device
  // label — both vendors cap it at 30 characters. Same cap as CreateMobileAppRequestSchema /
  // UpdateMobileAppRequestSchema (packages/shared-types/src/api/mobile-apps/index.ts), so the
  // wizard cannot mint a row the admin form then can't save.
  displayName: z
    .string()
    .max(30, { message: 'Store listing name cannot exceed 30 characters (App Store / Play limit)' })
    .optional(),

  // --- Apple ---
  appleTeamId: z.string().optional(),
  bundleId: z.string().optional(),
  /** ASC bundle id resource (apple-side bundleId resource record id). */
  ascBundleIdResourceId: z.string().optional(),
  ascApiKey: AscApiKeyInputSchema.optional(),
  apnsAuthKey: ApnsAuthKeyInputSchema.optional(),

  // --- Google ---
  googleDeveloperAccountId: z.string().optional(),
  /**
   * Play Console app id — the numeric id in a console URL
   * (`/developers/<dev>/app/<THIS>/…`).
   *
   * Lives on the RUN, not on `MobileApp`, because there is no column for it: the registry
   * carries `asc_app_id` for Apple but has no Play equivalent, and the public `play_store_url`
   * is keyed by package name, which is a different identifier entirely. Until someone decides
   * to promote it to a column, supplying it per-run is the only way to address a Play app
   * without scraping the console's app list for it.
   */
  googlePlayAppId: z.string().optional(),
  googleProjectId: z.string().optional(),
  androidPackage: z.string().optional(),
  /** Service account JSON (parsed) for Google Play + FCM. */
  googleServiceAccountJson: z.unknown().optional(),

  // --- OneSignal ---
  /** OneSignal *organization* API key (distinct from per-app REST key). */
  oneSignalOrgApiKey: z.string().optional(),

  // --- EAS ---
  easAccountName: z.string().optional(),
  expoSlug: z.string().optional(),

  // --- Branding ---
  splashBackgroundColor: z.string().optional(),
  splashResizeMode: z.string().optional(),
  adaptiveIconBackgroundColor: z.string().optional(),

  notes: z.string().optional()
})
export type ProvisioningRunInputs = z.infer<typeof ProvisioningRunInputsSchema>

// --- Run inputs as returned by the API ---
//
// The backend scrubs every secret before inputs leave it (`scrubInputs` in
// apps/backend/src/services/provisioning/scrub-secrets.ts). The response
// schema mirrors that scrubbed shape, so a secret field can only be typed as
// a stub: a raw string fails parse and fails typecheck (SCH-445).

/** What `scrubInputs` leaves in place of a secret value. */
export const ProvisioningSecretStubSchema = z.object({
  present: z.literal(true),
  length: z.number().int(),
  fingerprint: z.string(),
  redacted: z.literal(true)
})
export type ProvisioningSecretStub = z.infer<typeof ProvisioningSecretStubSchema>

export const ProvisioningRunResponseInputsSchema = ProvisioningRunInputsSchema.extend({
  ascApiKey: AscApiKeyInputSchema.extend({ privateKey: ProvisioningSecretStubSchema }).optional(),
  apnsAuthKey: ApnsAuthKeyInputSchema.extend({ p8: ProvisioningSecretStubSchema }).optional(),
  /** Either the whole JSON stubbed, or the parsed object with its key material stubbed. */
  googleServiceAccountJson: z
    .union([
      ProvisioningSecretStubSchema,
      z
        .object({
          private_key: ProvisioningSecretStubSchema.optional(),
          private_key_id: ProvisioningSecretStubSchema.optional()
        })
        .passthrough()
    ])
    .optional(),
  oneSignalOrgApiKey: ProvisioningSecretStubSchema.optional()
})
export type ProvisioningRunResponseInputs = z.infer<typeof ProvisioningRunResponseInputsSchema>

// --- Error envelope ---

export const ProvisioningErrorSchema = z.object({
  type: z.string(),
  message: z.string(),
  details: z.unknown().optional()
})
export type ProvisioningError = z.infer<typeof ProvisioningErrorSchema>

// --- Step ---

export const ProvisioningStepResponseSchema = z.object({
  id: z.string(),
  stepName: z.string(),
  stepOrder: z.number().int(),
  status: ProvisioningStepStatusSchema,
  attemptCount: z.number().int(),
  output: z.unknown().nullable().optional(),
  error: ProvisioningErrorSchema.nullable().optional(),
  startedAt: z.string().nullable().optional(),
  completedAt: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string()
})
export type ProvisioningStepResponse = z.infer<typeof ProvisioningStepResponseSchema>

// --- Run ---

export const ProvisioningRunResponseSchema = z.object({
  id: z.string(),
  mobileAppId: z.string().nullable(),
  initiatedByUserId: z.string().nullable(),
  status: ProvisioningRunStatusSchema,
  inputs: ProvisioningRunResponseInputsSchema,
  error: ProvisioningErrorSchema.nullable().optional(),
  startedAt: z.string().nullable().optional(),
  completedAt: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  steps: z.array(ProvisioningStepResponseSchema)
})
export type ProvisioningRunResponse = z.infer<typeof ProvisioningRunResponseSchema>
